import { useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Loader2, UserPlus } from "lucide-react";
import { SiGoogle } from "react-icons/si";
import { Helmet } from "react-helmet-async";

export function Register() {
  const [, setLocation] = useLocation();
  const { register } = useAuth();
  const { toast } = useToast();
  const apiURL = import.meta.env.VITE_API_URL || "http://localhost:4001";

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    fullName: "",
    password: "",
    confirmPassword: "",
    provider: "local",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // --- Validation functions ---
  const validateField = (name: string, value: string) => {
    let error = "";
    switch (name) {
      case "fullName":
        if (!value.trim() || value.length < 3) error = "Full name must be at least 3 characters long";
        break;
      // case "username":
      //   if (!value.trim() || value.length < 4) error = "Username must be at least 4 characters long";
      //   break;
      case "email":
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = "Please enter a valid email address";
        break;
      case "password":
        if (!value || value.length < 6) error = "Password must be at least 6 characters long";
        break;
      case "confirmPassword":
        if (value !== formData.password) error = "Passwords do not match";
        break;
    }
    setErrors(prev => ({ ...prev, [name]: error }));
    return error === "";
  };

  const validateForm = () => {
    const fields = ["fullName", "email", "password", "confirmPassword"];
    let isValid = true;
    fields.forEach(field => {
      if (!validateField(field, (formData as any)[field])) isValid = false;
    });
    return isValid;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    validateField(e.target.name, e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await register(
        formData.email,
        formData.fullName,
        formData.password,
        formData.provider
      );
      toast({ title: "Registration successful", description: "Welcome to GCEP!" });
      setLocation("/");
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Something went wrong",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Google OAuth Handler ---
  const handleGoogleOAuth = () => {
    window.location.href = `${apiURL}/api/auth/google`;
  };

  return (
    <>
      {/* Meta tags for SEO */}
      <Helmet>
        <title>Sign Up - GCEP</title>
        <meta name="description" content="Create an account on Gujarat Competitive Exam Preparation portal to access study resources and practice tests." />
        <meta name="keywords" content="GCEP, register, sign up, competitive exams, Gujarat, exam preparation" />
        <meta name="author" content="GCEP Team" />
        <meta property="og:title" content="GCEP - Sign Up" />
        <meta property="og:description" content="Create an account on Gujarat Competitive Exam Preparation portal to access study resources and practice tests." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-indigo-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-700 p-4">
        <div className="w-full max-w-md bg-white dark:bg-gray-900 shadow-md rounded-lg p-8">
          <h1 className="text-2xl font-bold text-center mb-6 text-gray-900 dark:text-white">
            Create Account
          </h1>

          {/* Google OAuth Button */}
          <div className="mb-6">
            <Button
              variant="outline"
              className="w-full flex items-center justify-center gap-2 h-11 mb-3"
              onClick={handleGoogleOAuth}
            >
              <SiGoogle className="w-5 h-5 text-red-500" />
              Continue with Google
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white dark:bg-gray-900 px-2 text-gray-500 dark:text-gray-400">
                  Or register with email
                </span>
              </div>
            </div>
          </div>

        {/* Traditional Registration Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="fullName">Full Name</Label>
            <Input id="fullName" name="fullName" type="text" placeholder="Enter your full name" value={formData.fullName} onChange={handleInputChange} onBlur={handleBlur} className="mt-2 w-full" />
            {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
          </div>

          {/* <div>
            <Label htmlFor="username">Username</Label>
            <Input id="username" name="username" type="text" placeholder="Choose a unique username" value={formData.username} onChange={handleInputChange} onBlur={handleBlur} className="mt-2 w-full" />
            {errors.username && <p className="text-red-500 text-sm mt-1">{errors.username}</p>}
          </div> */}

            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" name="email" type="email" placeholder="Enter your email" value={formData.email} onChange={handleInputChange} onBlur={handleBlur} className="mt-2 w-full" />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" name="password" type="password" placeholder="Create a strong password" value={formData.password} onChange={handleInputChange} onBlur={handleBlur} className="mt-2 w-full" />
              {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
            </div>

            <div>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input id="confirmPassword" name="confirmPassword" type="password" placeholder="Re-enter your password" value={formData.confirmPassword} onChange={handleInputChange} onBlur={handleBlur} className="mt-2 w-full" />
              {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
            </div>

            <Button type="submit" className="w-full gcep-primary-btn py-3 text-lg" disabled={isSubmitting}>
              {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Please wait...</> : <><UserPlus className="mr-2 h-4 w-4" />Register</>}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600 dark:text-gray-300">
              Already have an account?{" "}
              <button onClick={() => setLocation("/login")} className="text-blue-600 hover:text-blue-500 dark:text-blue-400 font-medium">
                Login here
              </button>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
